import React from 'react';
import { IPromoCodeDTO } from '../../../../../types/PromoCodeDTO';
import { getPromoCode } from '../api/getPromoCode';
import { InViewSpinner } from '../../../components/Spinners/InViewSpinner';
import { LocalizedPrice } from '../../../components/Price/Price';

enum PromoCodeState {
  INACTIVE,
  ACTIVE,
  INVALID,
  VALID,
  DURING_VALIDATION
}

interface PromoCodesProps {
  disabled?: boolean;
  prefilledPromoCode?: string;
  onPromoCodeApplied?: (promoCode: IPromoCodeDTO) => void;
  onPromoCodeRemoved?: () => void;
  onPromoCodeError?: (error: string) => void;
  onPromoCodeValidationInProgress?: (promoCode: string) => void;
}

export function PromoCodes(props: PromoCodesProps) {
  const [promoCodeState, setPromoCodeState] = React.useState<PromoCodeState>(
    props.prefilledPromoCode ? PromoCodeState.ACTIVE : PromoCodeState.INACTIVE
  );

  const [promoCode, setPromoCode] = React.useState(props.prefilledPromoCode || '');
  const [promoCodeData, setPromoCodeData] = React.useState<IPromoCodeDTO | null>(null);
  const [promoCodeError, setPromoCodeError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (props.prefilledPromoCode && props.prefilledPromoCode.length) {
      handlePromoCodeApply();
    }
  }, [props.prefilledPromoCode]);

  const handlePromoCodeApply = () => {
    setPromoCodeState(PromoCodeState.DURING_VALIDATION);
    props.onPromoCodeValidationInProgress?.(promoCode);

    getPromoCode(promoCode)
      .then(async (promoCodeDto: IPromoCodeDTO) => {
        props.onPromoCodeApplied?.(promoCodeDto);
        setPromoCodeState(PromoCodeState.VALID);
        setPromoCodeData(promoCodeDto);
        setPromoCode(promoCodeDto.code!);
      })
      .catch(() => {
        props.onPromoCodeError?.('Invalid promo code');
        setPromoCodeData(null);
        setPromoCodeError('Invalid promo code');
        setPromoCodeState(PromoCodeState.INVALID);
      });
  };

  const handleAddPromoCodeClick = () => {
    setPromoCodeState(PromoCodeState.ACTIVE);
  };

  const handlePromoCodeRemove = () => {
    props.onPromoCodeRemoved?.();
    setPromoCode('');
    setPromoCodeData(null);
    setPromoCodeError(null);
    setPromoCodeState(PromoCodeState.INACTIVE);
  };

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    handlePromoCodeApply();
  }

  return (
    <div className="flex max-w-60 items-center self-center">
      {promoCodeState == PromoCodeState.DURING_VALIDATION && (
        <div className="mx-10">
          <InViewSpinner></InViewSpinner>
        </div>
      )}
      {promoCodeState == PromoCodeState.INACTIVE && (
        <button
          className="font-semibold text-mint-medical-green hover:underline"
          onClick={() => {
            handleAddPromoCodeClick();
          }}>
          Add Promo Code
        </button>
      )}
      {promoCodeState == PromoCodeState.ACTIVE && (
        <form
          onSubmit={handleSubmit}
          className="flex flex-col">
          <input
            type="text"
            onChange={(event) => {
              setPromoCode(event?.target?.value);
            }}
            value={promoCode}
            className="w-12 rounded-md border-2 border-mint-medical-green-lighter p-2 text-center focus:border-mint-medical-green focus:outline-none md:w-36"></input>
          <input
            type="submit"
            value="Apply"
            disabled={!promoCode}
            className="font-semibold text-mint-medical-green hover:underline"></input>
        </form>
      )}
      {promoCodeState == PromoCodeState.VALID && (
        <div className="flex flex-col items-center">
          <span className="font-semibold text-mint-medical-green">{promoCode}</span>
          {promoCodeData!.coupon!.percentOff && (
            <span className="font-semibold text-mint-medical-green">
              (Discount -{promoCodeData!.coupon!.percentOff}%)
            </span>
          )}
          {promoCodeData!.coupon!.amountOff && (
            <span className="font-semibold text-mint-medical-green">
              (Discount -{promoCodeData?.coupon!.currency?.toLowerCase() == 'usd' ? '$' : '€'}
              {parseInt((promoCodeData!.coupon!.amountOff / 100).toString())})
            </span>
          )}
          {promoCodeData!.restrictions?.minimumAmount && (
            <div className="text-center">
              <span>Minimum amount to use that promo code is </span>
              <span className="font-semibold text-mint-medical-grey">
                <LocalizedPrice
                  valueInCents={promoCodeData!.restrictions!.minimumAmount!}
                  currency={promoCodeData!.restrictions!.minimumAmountCurrency!}></LocalizedPrice>
              </span>
              <span>.</span>
            </div>
          )}
          {promoCodeData!.coupon!.duration == 'once' && (
            <span className="text-center">That promo code will be used only once for the first payment.</span>
          )}

          {!props.disabled && (
            <button
              disabled={!!props.disabled}
              className="ml-2 font-semibold hover:underline"
              onClick={handlePromoCodeRemove}>
              Remove Code
            </button>
          )}
        </div>
      )}
      {promoCodeState == PromoCodeState.INVALID && (
        <div className="flex flex-col items-center">
          <span className="text-red-500">{promoCodeError}</span>
          <span className="font-semibold text-red-500">{promoCode}</span>
          {!props.disabled && (
            <button
              disabled={!!props.disabled}
              className="ml-2 font-semibold hover:underline"
              onClick={handlePromoCodeRemove}>
              Remove Code
            </button>
          )}
        </div>
      )}
    </div>
  );
}
